import { MetaWhatsappConnection } from "../Meta/MetaOnboardingModels";
import { AgentWorkflow, CompanyType } from "../../../config/companyCapabilities";
export type { AgentWorkflow } from "../../../config/companyCapabilities";

const DEFAULT_FIXED_SCHEDULE = {
  opening: "07:00",
  closing: "17:00",
};

const DEFAULT_VARIABLE_DAYS = {
  lunes: null,
  martes: null,
  miércoles: null,
  jueves: null,
  viernes: null,
  sábado: null,
  domingo: null,
};

function normalizeSchedule(data?: {
  mode?: "fixed" | "variable";
  fixed?: {
    opening?: string;
    closing?: string;
  };
  days?: {
    [day: string]: {
      opening: string;
      closing: string;
    } | null;
  };
}) {
  if (data?.mode === "variable") {
    return {
      mode: "variable" as const,
      days: {
        ...DEFAULT_VARIABLE_DAYS,
        ...(data.days ?? {}),
      },
    };
  }

  return {
    mode: "fixed" as const,
    fixed: {
      opening: data?.fixed?.opening ?? DEFAULT_FIXED_SCHEDULE.opening,
      closing: data?.fixed?.closing ?? DEFAULT_FIXED_SCHEDULE.closing,
    },
  };
}

export class AISettings {
  companyType: CompanyType;
  allowedIntegrations: string[];
  allowedWorkflows: AgentWorkflow[];
  name: string;
  nameBot: string;
  authMapos: boolean;
  companyId: string;
  idMapos: string;
  description: string;
  recommendations: string;
  urlMenu: string;
  websiteUrl: string;
  region: boolean;
  synonyms: string;
  schedule?: {
    mode: "fixed" | "variable";
    fixed?: {
      opening: string;
      closing: string;
    };
    days?: {
      [day: string]: {
        opening: string;
        closing: string;
      } | null;
    };
  };
  urlsMenu?: string[];
  takeOrdersEnabled?: boolean;
  websitePickupEnabled?: boolean;
  websiteDeliveryEnabled?: boolean;
  whatsappDebounceSeconds?: number;
  agentWorkflow: AgentWorkflow;

  constructor(data: any) {
    this.companyType = data?.companyType === "MAPOS" ? "MAPOS" : "BASIC";
    this.allowedIntegrations = Array.isArray(data?.allowedIntegrations)
      ? data.allowedIntegrations
      : [];
    const configuredWorkflows = Array.isArray(data?.allowedWorkflows)
      ? data.allowedWorkflows
      : this.companyType === "MAPOS"
        ? ["NONE", "MAPOS_RESTAURANT"]
        : ["NONE"];
    this.allowedWorkflows = configuredWorkflows.filter(
      (workflow: AgentWorkflow) =>
        workflow === "NONE" || workflow === "MAPOS_RESTAURANT" || workflow === "LOANS" || workflow === "EXTERNAL_BOT",
    );
    if (
      this.companyType === "MAPOS" &&
      (configuredWorkflows as string[]).includes("MAPOS_RESTAURANT_V2") &&
      !this.allowedWorkflows.includes("MAPOS_RESTAURANT")
    ) {
      this.allowedWorkflows.push("MAPOS_RESTAURANT");
    }
    if (this.allowedWorkflows.length === 0) this.allowedWorkflows = ["NONE"];
    this.name = data?.name ?? "";
    this.nameBot = data?.nameBot ?? "";
    this.authMapos = data?.authMapos ?? false;
    this.companyId = data?.companyId ?? "";
    this.idMapos = data?.idMapos ?? "";
    this.description = data?.description ?? "";
    this.recommendations = data?.recommendations ?? "";
    this.urlMenu = data?.urlMenu ?? "";
    this.websiteUrl = data?.websiteUrl || "";
    this.region = false;
    this.synonyms = data?.synonyms ?? "";
    this.schedule = normalizeSchedule(data?.schedule);
    this.urlsMenu = data?.urlsMenu || [];
    this.takeOrdersEnabled = data?.takeOrdersEnabled ?? true;
    this.websitePickupEnabled = data?.websitePickupEnabled ?? true;
    this.websiteDeliveryEnabled = data?.websiteDeliveryEnabled ?? true;
    const rawDebounceSeconds = data?.whatsappDebounceSeconds;
    const debounceSeconds =
      rawDebounceSeconds === undefined ||
      rawDebounceSeconds === null ||
      String(rawDebounceSeconds).trim() === ""
        ? Number.NaN
        : Number(rawDebounceSeconds);
    this.whatsappDebounceSeconds =
      Number.isFinite(debounceSeconds) && debounceSeconds >= 0
        ? Math.min(60, Math.round(debounceSeconds))
        : 7;
    const requestedWorkflow =
      data?.agentWorkflow === "LOANS"
        ? "LOANS"
        : data?.agentWorkflow === "EXTERNAL_BOT"
          ? "EXTERNAL_BOT"
        : data?.agentWorkflow === "MAPOS_RESTAURANT" ||
            data?.agentWorkflow === "MAPOS_RESTAURANT_V2"
          ? "MAPOS_RESTAURANT"
          : "NONE";
    this.agentWorkflow = this.allowedWorkflows.includes(requestedWorkflow)
      ? requestedWorkflow
      : this.allowedWorkflows[0];
  }
}

export class WSSettings {
  accessToken: string;
  phoneNumberId: string;
  whatsappId: string;
  metaWebhookProxyUrl: string;
  maposBotWebhookUrl: string;
  maposBotWebhookSecret: string;
  maposBotWebhookSecretConfigured: boolean;

  constructor(data: Partial<WSSettings>) {
    this.accessToken = data.accessToken ?? "";
    this.phoneNumberId = data.phoneNumberId ?? "";
    this.whatsappId = data.whatsappId ?? "";
    this.metaWebhookProxyUrl = data.metaWebhookProxyUrl || "";
    this.maposBotWebhookUrl = data.maposBotWebhookUrl || "";
    this.maposBotWebhookSecret = "";
    this.maposBotWebhookSecretConfigured = data.maposBotWebhookSecretConfigured === true;
  }
}

export class AllConfigDataModel {
  aiSettings: AISettings;
  wsSettings: WSSettings;
  metaConnections?: {
    whatsapp?: MetaWhatsappConnection;
  };

  constructor(data: { [key: string]: any }) {
    this.aiSettings = new AISettings({
      companyType: data.companyType,
      allowedWorkflows: data.allowedWorkflows,
      name: data.name,
      nameBot: data.nameBot,
      authMapos: data.authMapos,
      companyId: data.companyId,
      idMapos: data.idMapos,
      description: data.description,
      recommendations: data.recommendations,
      urlMenu: data.urlMenu,
      websiteUrl: data.websiteUrl,
      schedule: data.schedule,
      urlsMenu: data.urlsMenu,
      takeOrdersEnabled: data.takeOrdersEnabled,
      websitePickupEnabled: data.websitePickupEnabled,
      websiteDeliveryEnabled: data.websiteDeliveryEnabled,
      whatsappDebounceSeconds: data.whatsappDebounceSeconds,
      agentWorkflow: data.agentWorkflow,
    });

    this.wsSettings = new WSSettings({
      accessToken: data.accessToken,
      phoneNumberId: data.phoneNumberId,
      whatsappId: data.whatsappId,
      metaWebhookProxyUrl: data.metaWebhookProxyUrl,
    });
    this.metaConnections = data.metaConnections;
  }
}

export type ProductSyncResult = {
  catalogSynced: boolean;
  categoriesSynced: number;
  productsSynced: number;
  embeddingsSynced: number;
  skipped: boolean;
};
